// Optional per-user document branding (logo + accent colour) applied to both the
// RAMS preview (ReportDocument) and the PDF (pdfHtml). Stored locally per browser.
// Logos are raster-only and size-capped so the embedded data URI is safe to inline
// (no SVG — it can carry script) and small enough not to bloat the PDF.

export type Brand = {
  logo: string;
  /** Accent colour — section underlines + header stripe. */
  color: string;
  /** Header band background. Empty = the default navy gradient. */
  headerColor: string;
};
export const EMPTY_BRAND: Brand = { logo: "", color: "", headerColor: "" };

/** The RamsDocs default accent — used whenever no brand colour is set. */
export const DEFAULT_ACCENT = "#1ba37b";

const HEX = /^#[0-9a-fA-F]{6}$/;

/** A valid 6-digit hex (lowercased), or "" to fall back to the default accent. */
export function brandColor(c: unknown): string {
  return typeof c === "string" && HEX.test(c.trim()) ? c.trim().toLowerCase() : "";
}

/** Only our own resized raster data URIs — never an arbitrary URL or SVG. */
export function safeLogo(d: unknown): string {
  return typeof d === "string" &&
    /^data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+$/.test(d) &&
    d.length <= 180000
    ? d
    : "";
}

/** Sanitise a stored/untrusted brand blob into a safe Brand. */
export function sanitizeBrand(raw: unknown): Brand {
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return { logo: safeLogo(b.logo), color: brandColor(b.color), headerColor: brandColor(b.headerColor) };
}

/** True when there's anything to apply. */
export function hasBrand(b: Brand | undefined): boolean {
  return !!b && (!!safeLogo(b.logo) || !!brandColor(b.color) || !!brandColor(b.headerColor));
}

/** Header band colours. With no header colour set, returns the navy default (caller
 *  keeps the gradient class). Otherwise picks readable text via relative luminance so
 *  a light header (e.g. to match a white-background logo) gets dark text. */
export type HeaderTheme = { bg: string; fg: string; muted: string; faint: string };
export function headerTheme(headerColor: unknown): HeaderTheme {
  const c = brandColor(headerColor);
  if (!c) return { bg: "", fg: "#ffffff", muted: "rgba(255,255,255,0.7)", faint: "rgba(255,255,255,0.3)" };
  const r = parseInt(c.slice(1, 3), 16);
  const g = parseInt(c.slice(3, 5), 16);
  const b = parseInt(c.slice(5, 7), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6
    ? { bg: c, fg: "#0f172a", muted: "rgba(15,23,42,0.62)", faint: "rgba(15,23,42,0.22)" }
    : { bg: c, fg: "#ffffff", muted: "rgba(255,255,255,0.72)", faint: "rgba(255,255,255,0.3)" };
}
